import assert from '@/sui/tools/assert'
import units from '@/sui/data/units'
import notes from '@/sui/data/sheets/edit/notes'
import fields from '@/sui/data/sheets/edit/fields'
import moment from 'moment'

function checkPatientOrigin (result, sheet, year) {
  const type = sheet[fields.unitType]
  const origin = sheet[fields.patientOrigin]
  if (type == null || origin == null) {
    return
  }

  const ehp = year === 2019 ? 6 : 7

  const note = result[fields.patientOrigin]
  if (type.match(/^AD/) != null) {
    if (origin === ehp) {
      const birthdate = notes.getDate(sheet, fields.birthdate)
      const forty = new Date(year - 40, 0, 1)
      if (birthdate != null && birthdate > forty) {
        note.type = 'warning'
        note.tooltip = `Le patient paraît jeune pour provenir d'un EHPAD`
      }
    }
  } else {
    const mix = type.match(/^MIX/) != null
    const managment = sheet[fields.mixManagment]
    if (mix && managment == null) {
      return
    }
    if (((mix && managment === 1) || type.match(/^PED/))) {
      if (origin === ehp) {
        note.type = 'error'
        note.tooltip = `Un patient de pédiatrie ne peut pas provenir d'un EHPAD`
      }
    } else if (year === 2019 &&
               ((mix && managment === 2) || type.match(/^NEO/))) {
      const ssr = 3
      const psy = 4
      const sld = 5

      const forbidden = [ ssr, psy, sld, ehp ]
      if (forbidden.indexOf(origin) !== -1) {
        note.type = 'error'
        note.tooltip = `Provenance incorrecte pour un nouveau-né`
      }
    }
  }
}

function checkInfOrigin (result, sheet, field, text, ped) {
  if (!ped) {
    return
  }

  const origin = sheet[field]
  if (origin === 4) {
    const note = result[field]
    note.type = 'error'
    note.tooltip = `${text} ne peut pas provenir d'un EHPAD pour un enfant ou
un nouveau-né`
  }
}

function checkBactOrigin (result, sheet, ped) {
  checkInfOrigin(result, sheet, fields.bactOrigin, 'La bactériémie', ped)
}

function checkMicroOrg (result, sheet, ref, text, explaur, field) {
  const moField = field == null ? fields.microOrg : field
  const microOrgs = sheet[moField]
  if (microOrgs.length > 0) {
    const microOrg = microOrgs[0]
    const reference = sheet[ref]
    if (reference != null && microOrg[fields.microOrgCode] == null) {
      const note = result[ref]
      if (note.type !== 'error') {
        note.type = 'error'
        note.tooltip = `${text} doit comporter au moins un micro-organisme.`
      }
    }

    if (explaur == null) {
      return
    }

    const moNotes = result[moField]
    const expected = explaur.labels.findIndex(
      l => l.ut === explaur.ut && l.uf === explaur.uf
    )
    microOrgs.forEach(
      (mo, i) => {
        const moNote = moNotes[i]
        const eid = mo[fields.explaurID]
        if (eid != null) {
          const real = explaur.labels.findIndex(
            l => eid.startsWith(l.labl)
          )
          const note = moNote[fields.explaurID]
          if (note.type !== 'error' && real !== expected) {
            note.type = 'error'
            note.tooltip = `L'écouvillon choisi ne vient pas
de la même Uf que celle spécifiée dans la fiche. Merci de vérifier
les champs «Code du service» et «Identifiant du service».`
          }
        }
      }
    )
  }
}

function checkBactMicroOrg (result, sheet, ref, explaur) {
  checkMicroOrg(result, sheet, ref, 'Un épisode de bactériémie', explaur)
}

function checkPortageMO (result, sheet) {
  const portage = sheet[fields.portageBmrBhre]
  if (portage != null && portage === 1) {
    const mo = sheet[fields.bmrBhreMicroOrgs]
    const error = mo.length === 0 || mo[0][fields.microOrgCode] == null
    if (error) {
      const note = result[fields.portageBmrBhre]
      if (note.type !== 'error') {
        note.type = 'error'
        note.tooltip = `Le portage BMR/BHre doit comporter au moins un
un micro-organisme `
      }
    }
  }
}

function getExplaurUnit (sheet, surveillance) {
  const uy = units[surveillance.year]
  const ut = uy.find(sheet.unitType)
  return {
    uf: sheet.uf,
    ut: ut == null ? null : ut.code,
    labels: surveillance.getExplaurLabels()
  }
}

function checkUfs (result, sheet, module, surveillance) {
  if (sheet.uf == null || sheet.uf === '' || sheet.unitType == null || sheet.unitType === '') {
    return
  }

  const uy = units[surveillance.year]
  const ut = uy.find(sheet.unitType)

  if (surveillance.getUf(module, ut.index, sheet.uf) == null) {
    const nut = result[fields.unitType]
    const nuf = result[fields.uf]

    nut.type = nuf.type = 'error'
    nut.tooltip = nuf.tooltip = `Le service ${sheet.uf} (${ut.code}) n'est pas inscrit à cette modalité.`
  }
}

function beforeEpisod (value, note) {
  const fmt = 'DD/MM/YYYY'
  const ep = value == null ? null : moment(value, fmt).subtract(7, 'days')
  const epf = ep == null ? '' : ep.format(fmt)
  return {
    date: ep == null ? null : ep.toDate(),
    text: `date de l'épisode moins 7 jours (soit le ${epf})`,
    note: note
  }
}

function checkEpisodAndEntry (episod, entry) {
  if (episod.date == null || entry.date == null) {
    return
  }

  const duration = moment(episod.date).diff(moment(entry.date), 'days')
  if (duration >= 0) {
    return
  }

  const error = duration < -14
  const notes = [entry.note, episod.note]
  for (let i = 0; i !== notes.length; ++i) {
    const note = notes[i]
    if (note.type != null && (!error || note.type === 'error')) {
      continue
    }

    note.type = error ? 'error' : 'warning'
    note.tooltip = error
      ? `Incohérence : la date de l'épisode infectieux est antérieure de plus de 14 jours à la date
d'entrée dans le service.`
      : `Incohérence possible : la date de l'épisode infectieux est antérieure à la date
d'entrée dans le service.`
  }
}

function checkPatientBirthdate (result, sheet) {
  const birthdate = sheet[fields.birthdate]
  const entry = sheet[fields.entrydate]
  const type = sheet[fields.unitType]
  if (type != null && type.match(/^AD/) != null && birthdate === entry) {
    const notes = [
      result[fields.birthdate],
      result[fields.unitType]
    ]
    for (let i = 0; i !== notes.length; ++i) {
      const n = notes[i]
      if (n.type !== 'error') {
        n.type = 'error'
        n.tooltip = `La date de naissance est incorrecte pour un patient
adulte`
      }
    }
  }
}

function atLeastOne (sheet, fields) {
  assert.isArray(fields)
  assert.isObject(sheet)

  for (let i = 0; i < fields.length; i++) {
    const field = fields[i]
    const values = sheet[field]

    if (values != null) {
      const keys = Object.keys(values)
      for (let j = 0; j < keys.length; j++) {
        const key = keys[j]
        if (values[key]) {
          return true
        }
      }
    }
  }
  return false
}

export default {
  checkEpisodAndEntry,
  checkPatientBirthdate,
  checkPatientOrigin,
  checkBactOrigin,
  checkInfOrigin,
  checkMicroOrg,
  checkBactMicroOrg,
  checkPortageMO,
  checkUfs,
  getExplaurUnit,
  beforeEpisod,
  checkPickList: {
    atLeastOne
  }
}
